import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from "@chakra-ui/icons";
import {
  Box,
  Flex,
  IconButton,
  Table,
  TableCellProps,
  TableContainer,
  TableContainerProps,
  Tbody,
  Td,
  Text,
  Thead,
  Tr
} from "@chakra-ui/react";
import { SkeletonLoading } from "components/skeleton-loading";
import { Fragment, ReactNode, useEffect, useState } from "react";

export interface CustomTableColorColumn<T = any> {
  name: string | JSX.Element;
  render: (row: T, index: number) => any;
  onClick?: () => void;
  isVisible?: boolean;
  headerStyles?: TableCellProps;
  cellStyles?: (row: T, index: number) => TableCellProps;
  icon?: ReactNode;
}

export interface InnerRowRenderProps {
  row: any;
  rowIndex: number;
  columnsLength: number;
  activeIndex: number | undefined;
  [k: string]: any;
}
export interface CustomTableColorProps {
  color?: string;
  isLoading?: boolean;
  columns: CustomTableColorColumn[];
  rows: any[];
  rowsLength?: number;
  currentPage?: number;
  onPrevPage?: () => void;
  onNextPage?: () => void;
  onFirstPage?: () => void;
  onSetPage?: (page: number) => void;
  onClickRow?: (row: any, i: number) => void;
  loadingRowsCount?: number;
  containerProps?: TableContainerProps;
  headerStyle?: TableCellProps;
  pageSize?: number;
  showControls?: boolean;
  InnerRowRender?: (props: InnerRowRenderProps) => any;
  innerRowRenderProps?: any;
  onChangeIndexPage?: () => void;
  emptyMessage?: string;
}

export function CustomTableColor({
  isLoading,
  color = "var(--chakra-colors-primary-400)",
  columns,
  rows = [],
  rowsLength,
  currentPage = 0,
  loadingRowsCount = 10,
  onPrevPage,
  onNextPage,
  onFirstPage,
  onSetPage,
  onClickRow,
  containerProps,
  headerStyle,
  pageSize = 10,
  showControls = true,
  InnerRowRender,
  innerRowRenderProps,
  onChangeIndexPage,
  emptyMessage
}: CustomTableColorProps) {
  const [index, setIndex] = useState(currentPage);

  const filteredColumns = columns.filter(
    (col) => col.isVisible == null || col.isVisible
  );

  const withOutExternalControls =
    !onSetPage && (!onFirstPage || !onNextPage || !onPrevPage);

  const prevPage = () => {
    if (onSetPage) onSetPage(index - 1);
    else if (!withOutExternalControls) onPrevPage!();
    else setIndex((i) => i - 1);
  };

  const nextPage = () => {
    if (onSetPage) onSetPage(index + 1);
    else if (!withOutExternalControls) onNextPage!();
    else setIndex((i) => i + 1);
  };

  const firstPage = () => {
    if (onSetPage) onSetPage(0);
    else if (!withOutExternalControls) onFirstPage!();
    else setIndex(0);
  };

  const paginedRows = paginateRows(rows, pageSize);

  const selectedPage = withOutExternalControls
    ? (paginedRows[index] ?? [])
    : rows;

  const totalPages =
    !withOutExternalControls && rowsLength != null
      ? Math.ceil(rowsLength / pageSize)
      : paginedRows.length;

  const { pageIndexesArr } = getPageIndexes(totalPages, index);

  useEffect(() => {
    setIndex(currentPage);
  }, [currentPage]);

  const lastPage = () => {
    if (onSetPage) onSetPage(totalPages - 1);
    else if (withOutExternalControls) setIndex(totalPages - 1);
  };

  useEffect(() => {
    if (withOutExternalControls) setIndex(0);
  }, [rows.length, withOutExternalControls]);

  useEffect(() => {
    onChangeIndexPage?.();
  }, [index, onChangeIndexPage]);

  return (
    <TableContainer
      bg="#fff"
      w="100%"
      borderRadius="10px"
      borderLeft="1px solid #EAEAEA"
      borderRight="1px solid #EAEAEA"
      borderBottom="1px solid #EAEAEA"
      borderTop={`4px solid ${color}`}
      {...containerProps}
    >
      <Table overflow="visible">
        <Thead color="#656F7D" fontSize="14px" fontWeight="medium">
          <Tr>
            {filteredColumns.map((column, index) => {
              return (
                <Td
                  key={`column-${index}`}
                  border="none"
                  onClick={column.onClick}
                  cursor={column.onClick ? "pointer" : undefined}
                  _hover={column.onClick ? { bg: "gray.50" } : undefined}
                  {...headerStyle}
                  {...column.headerStyles}
                >
                  <Flex align="center" w="100%" justifyContent="space-between">
                    {column.name}
                    {column.onClick &&
                      (column.icon ?? <ChevronDownIcon ml={1} w={5} h={5} />)}
                  </Flex>
                </Td>
              );
            })}
          </Tr>
        </Thead>
        <Tbody>
          {isLoading ? (
            Array.from({ length: loadingRowsCount }).map((_, index) => {
              return (
                <Tr key={`Tr-${index}`}>
                  {filteredColumns.map((_, index) => {
                    return (
                      <Td key={`Td-${index}`} p="0px">
                        <SkeletonLoading
                          key={index}
                          isLoading
                          h="59px"
                          w="100%"
                        />
                      </Td>
                    );
                  })}
                </Tr>
              );
            })
          ) : !selectedPage.length && emptyMessage ? (
            <Box px="24px" py="10px">
              {emptyMessage}
            </Box>
          ) : (
            selectedPage.map((row, index) => {
              const isLast = rows.length === index + 1;
              return (
                <Fragment key={`row-${index}`}>
                  <Tr
                    key={row.id || `row-${index}`}
                    bg="#fff"
                    onClick={() => {
                      onClickRow?.(row, index);
                    }}
                    _hover={{ bg: "rgba(45, 61, 133, 0.03)" }}
                    cursor={onClickRow ? "pointer" : undefined}
                  >
                    {filteredColumns.map((cell, i) => {
                      return (
                        <Td
                          key={`${row.id}-cell-${i}`}
                          borderBottom={isLast ? "none" : "1px solid #EAEAEA"}
                          {...cell.cellStyles?.(row, index)}
                        >
                          {cell.render(row, index)}
                        </Td>
                      );
                    })}
                  </Tr>
                  {InnerRowRender ? (
                    <InnerRowRender
                      key={index + "-aditionalContent"}
                      columnsLength={columns.length}
                      row={row}
                      rowIndex={index}
                      {...innerRowRenderProps}
                    />
                  ) : null}
                </Fragment>
              );
            })
          )}
        </Tbody>
      </Table>
      {showControls ? (
        <Flex justifyContent="flex-end" alignItems="center" px="24px" py="16px">
          <IconButton
            variant="ghost"
            aria-label="proxima pagina"
            icon={<ArrowLeftIcon w="9px" h="9px" />}
            onClick={() => {
              firstPage();
            }}
            isDisabled={index === 0 || isLoading}
          />
          <IconButton
            variant="ghost"
            aria-label="proxima pagina"
            icon={<ChevronLeftIcon />}
            onClick={() => {
              prevPage();
            }}
            isDisabled={index === 0 || isLoading}
          />

          {pageIndexesArr.map((pageNumber) => {
            const active = index === pageNumber;
            return (
              <Text
                key={pageNumber}
                mx="8px"
                p="4px"
                borderRadius="10px"
                bg={active ? "gray.100" : ""}
                onClick={() => {
                  if (withOutExternalControls) setIndex(pageNumber);
                  else onSetPage?.(pageNumber);
                }}
                cursor={"pointer"}
              >
                {pageNumber + 1}
              </Text>
            );
          })}

          <IconButton
            variant="ghost"
            aria-label="proxima pagina"
            icon={<ChevronRightIcon />}
            onClick={() => {
              nextPage();
            }}
            isDisabled={
              (!withOutExternalControls
                ? rowsLength != null && index >= totalPages - 1
                : !paginedRows[index + 1]) || isLoading
            }
          />
          {(withOutExternalControls || rowsLength != null) && (
            <IconButton
              variant="ghost"
              aria-label="última pagina"
              icon={<ArrowRightIcon w="9px" h="9px" />}
              onClick={() => {
                lastPage();
              }}
              isDisabled={index >= totalPages - 1 || isLoading}
            />
          )}
        </Flex>
      ) : null}
    </TableContainer>
  );
}

const paginateRows = (rows: any[], pageSize: number) => {
  let pageAcumulator: any[] = [];
  const paginedRows: any[][] = rows.reduce((acc, curr, index) => {
    const lastRow = index + 1 === rows.length;
    // Append row in page acc
    pageAcumulator.push(curr);
    // If page acc length equals page size catch de page into paginedRows and reset the page acc
    if (pageAcumulator.length === pageSize) {
      acc.push(pageAcumulator);
      pageAcumulator = [];
    }
    // Catch the last page if smaller of pageSize
    if (lastRow && pageAcumulator.length) {
      acc.push(pageAcumulator);
    }
    return acc;
  }, [] as any[][]);
  return paginedRows;
};

function getPageIndexes(totalPages: number, currentIndex: number) {
  let pageIndexesArr =
    totalPages > 1
      ? Array.from({ length: totalPages }, (_, i) => i)
      : [currentIndex];

  if (totalPages > 1) {
    if (currentIndex - 2 >= 0 && totalPages > 5) {
      const cut: number[] = pageIndexesArr.slice(
        currentIndex - 2,
        currentIndex + 3
      );
      if (cut.length < 5)
        pageIndexesArr = pageIndexesArr.slice(totalPages - 5, totalPages);
      else pageIndexesArr = cut;
    } else pageIndexesArr = pageIndexesArr.slice(0, 5);
  }
  return { pageIndexesArr };
}
